// Client-side brand-color extraction (R22, brand-colors option B).
//
// Extract the dominant colors from a graph/screenshot image ENTIRELY in the browser — the
// image is drawn to a canvas, pixels are sampled and quantized, and only the resulting hex
// values are ever sent to the server. The image itself never leaves the device (privacy +
// zero server cost). Near-white / near-black / near-gray pixels are skipped so we return
// actual brand hues, ordered most-frequent first.

const _hex2 = (n) => n.toString(16).padStart(2, "0");
export const rgbToHex = (r, g, b) => "#" + _hex2(r) + _hex2(g) + _hex2(b).toUpperCase();

// Perceptual-ish distance so we don't return two near-identical hues.
function dist(a, b) {
  const dr = a[0] - b[0], dg = a[1] - b[1], db = a[2] - b[2];
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

function isBoring([r, g, b]) {
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  if (max >= 244 && min >= 244) return true;   // near-white
  if (max <= 24) return true;                   // near-black
  if (max - min <= 18) return true;             // near-gray (low saturation)
  return false;
}

// Load a File/Blob/dataURL into an HTMLImageElement.
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    if (src instanceof Blob) img.src = URL.createObjectURL(src);
    else img.src = src;
  });
}

// Returns up to `count` dominant, distinct, non-boring colors as #RRGGBB (most frequent first).
export async function extractPaletteFromImage(fileOrSrc, count = 6) {
  const img = await loadImage(fileOrSrc);
  const W = 120, H = Math.max(1, Math.round((img.height / img.width) * W)) || 120;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, W, H);
  let data;
  try { data = ctx.getImageData(0, 0, W, H).data; }
  catch (e) { return []; } // tainted canvas / decode failure → let caller fall back to manual

  // Quantize into 32-value buckets per channel and count frequency of non-boring pixels.
  const buckets = new Map();
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 128) continue; // transparent
    const r = data[i], g = data[i + 1], b = data[i + 2];
    if (isBoring([r, g, b])) continue;
    const key = ((r >> 5) << 6) | ((g >> 5) << 3) | (b >> 5);
    const e = buckets.get(key);
    if (e) { e.r += r; e.g += g; e.b += b; e.n += 1; }
    else buckets.set(key, { r, g, b, n: 1 });
  }
  const ranked = [...buckets.values()]
    .sort((a, b) => b.n - a.n)
    .map((e) => [Math.round(e.r / e.n), Math.round(e.g / e.n), Math.round(e.b / e.n)]);

  // Greedily keep colors that are visually distinct from those already chosen.
  const chosen = [];
  for (const c of ranked) {
    if (chosen.every((k) => dist(c, k) > 40)) chosen.push(c);
    if (chosen.length >= count) break;
  }
  return chosen.map(([r, g, b]) => rgbToHex(r, g, b));
}
